import { CalendarCheck2 } from 'lucide-react'
import { Button } from '@/components/ui/Button/Button'
import { cn } from '@/utils/helpers'
import { marketplaceConversationUrl } from '@/utils/conversation.utils'
import { AppointmentStatus } from '@/components/molecules/AppointmentStatus'
import { TimeStatus } from '@/models/generated/graphql'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger
} from '@/components/ui/Drawer/Drawer'
import { ConsentCard } from '../ConsentCard'

type AppointmentInfoSectionProps = {
  participantName: string
  participantAvatar?: string
  scheduledDate: string // ISO format
  isParticipantPresent: boolean
  conversationId: string
  timeStatus: TimeStatus
  onJoinMeeting: () => void
  isJoining?: boolean
  className?: string
}

export const AppointmentInfoSection = ({
  participantName,
  participantAvatar,
  scheduledDate,
  isParticipantPresent,
  conversationId,
  timeStatus,
  onJoinMeeting,
  isJoining = false,
  className
}: AppointmentInfoSectionProps) => {
  // Format date in French: "23 octobre 2025 à 16h30"
  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate)
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }
    const formattedDate = new Intl.DateTimeFormat('fr-FR', options).format(date)
    const time = date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    })
    return `${formattedDate} à ${time}`
  }

  // Build conversation URL using utility function
  const conversationUrl = marketplaceConversationUrl(conversationId)

  return (
    <div className={cn('flex flex-col items-center gap-6 lg:gap-11', className)}>
      {/* Title */}
      <div className="flex flex-col items-center gap-6">
        <h1 className="text-lg lg:text-2xl font-semibold text-center">
          Rendez-vous My Job Glasses avec {participantName}
        </h1>

        {/* Scheduled Date */}
        <div className="flex items-center gap-2 text-sm lg:text-xl font-light">
          <CalendarCheck2 className="h-4 w-4 lg:h-6 lg:w-6" />
          <span>Programmé le {formatDate(scheduledDate)}</span>
        </div>
      </div>

      {/* Avatar and Status */}
      <AppointmentStatus
        timeStatus={timeStatus}
        participantName={participantName}
        participantAvatar={participantAvatar}
        isParticipantPresent={isParticipantPresent}
      />

      {/* Join Button */}
      <div className="flex flex-col gap-4 w-full lg:w-1/2">
        <Button onClick={onJoinMeeting} disabled={timeStatus !== TimeStatus.Current || isJoining}>
          Rejoindre la visio
        </Button>
        <Drawer>
          <DrawerTrigger asChild className="lg:hidden mb-4">
            <Button variant="secondary">En savoir plus sur la visio intégrée</Button>
          </DrawerTrigger>
          <DrawerContent className="bg-white max-h-[95dvh]">
            <DrawerHeader>
              <DrawerTitle className="text-start">En savoir plus sur la visio intégrée</DrawerTitle>
            </DrawerHeader>
            <div className="no-scrollbar overflow-y-auto px-4">
              <ConsentCard />
            </div>
            <DrawerFooter>
              <DrawerClose asChild>
                <Button className="w-full" variant="secondary">
                  Fermer
                </Button>
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>

        <Button asChild variant="link" className="hidden lg:flex">
          <a href={conversationUrl}>Retour sur la conversation</a>
        </Button>
      </div>
    </div>
  )
}
